import { useState } from "react";
import {
  Coins, Truck, UsersThree, TrendUp, Gear, CaretDown,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import HoverTip from "@/components/HoverTip";

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="bg-white border border-brand-line rounded-md p-6 md:p-8">
    <div className="flex items-start gap-4 mb-6 pb-5 border-b border-brand-line">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-brand-cream text-brand-forest shrink-0">
        <Icon weight="duotone" size={22} />
      </span>
      <div>
        <h3 className="font-serif text-2xl text-brand-forest leading-tight">{title}</h3>
        {subtitle && <p className="text-sm text-brand-ink/60 mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </section>
);

function Field({ id, label, suffix, prefix, value, onChange, step = 1, type = "number", testId, hint, helpId }) {
  return (
    <div className="flex flex-col gap-2">
      <HoverTip id={helpId || id} label={label} htmlFor={id} />
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm font-medium pointer-events-none">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          data-testid={testId || `input-${id}`}
          type={type}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
          className={`bg-white border-brand-line focus-visible:ring-2 focus-visible:ring-brand-forest rounded-md h-11 ${
            prefix ? "pl-7" : ""
          } ${suffix ? "pr-10" : ""}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted text-xs font-semibold uppercase tracking-wider pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-brand-muted">{hint}</p>}
    </div>
  );
}

function PassiveIncomeSection({ inputs, update }) {
  return (
    <Section
      icon={Coins}
      title="Corporate Passive Income"
      subtitle="The money sitting in retained earnings — being taxed at 50.17% today."
    >
      <Field
        id="annual_contribution"
        label="Annual contribution"
        prefix="$"
        value={inputs.annual_contribution}
        onChange={(v) => update("annual_contribution", v)}
        step="1000"
        testId="input-annual-contribution"
        hint="Retained earnings directed into savings / IBC premium each year."
      />
      <Field
        id="horizon_years"
        label="Projection horizon"
        suffix="YRS"
        value={inputs.horizon_years}
        onChange={(v) => update("horizon_years", v || 1)}
        step="1"
        testId="input-horizon-years"
        hint="We always report 5-yr and 10-yr benchmarks too."
      />
    </Section>
  );
}

function InterestRecaptureSection({ inputs, update }) {
  return (
    <Section
      icon={Truck}
      title="Interest Recapture"
      subtitle="Every dollar going to Ford Credit, CWB, or a bank — you'd recapture inside your own policy."
    >
      <Field
        id="num_vehicles"
        label="Financed vehicles / equipment"
        suffix="UNITS"
        value={inputs.num_vehicles}
        onChange={(v) => update("num_vehicles", v || 0)}
        step="1"
        testId="input-num-vehicles"
      />
      <Field
        id="avg_monthly_payment"
        label="Avg. monthly payment"
        prefix="$"
        value={inputs.avg_monthly_payment}
        onChange={(v) => update("avg_monthly_payment", v)}
        step="10"
        testId="input-avg-monthly-payment"
      />
      <Field
        id="avg_loan_interest_rate_pct"
        helpId="avg_loan_interest_rate_pct"
        label="Avg. loan interest rate"
        suffix="%"
        value={(inputs.avg_loan_interest_rate * 100).toFixed(2)}
        onChange={(v) => update("avg_loan_interest_rate", (v || 0) / 100)}
        step="0.1"
        testId="input-avg-loan-rate"
      />
      <Field
        id="avg_loan_term_years"
        label="Avg. loan term"
        suffix="YRS"
        value={inputs.avg_loan_term_years}
        onChange={(v) => update("avg_loan_term_years", v || 1)}
        step="1"
        testId="input-avg-loan-term"
      />
    </Section>
  );
}

function RetentionSection({ inputs, update }) {
  return (
    <Section
      icon={UsersThree}
      title="Golden Handcuffs · Retention"
      subtitle="Key foremen, estimators, project managers — what it costs if they walk."
    >
      <Field
        id="num_key_employees"
        label="Key employees"
        suffix="PEOPLE"
        value={inputs.num_key_employees}
        onChange={(v) => update("num_key_employees", v || 0)}
        step="1"
        testId="input-num-key-employees"
      />
      <Field
        id="annual_retention_bonus"
        label="Annual retention allocation (each)"
        prefix="$"
        value={inputs.annual_retention_bonus}
        onChange={(v) => update("annual_retention_bonus", v)}
        step="500"
        testId="input-annual-retention-bonus"
      />
      <Field
        id="turnover_cost_per_employee"
        label="Turnover cost per employee"
        prefix="$"
        value={inputs.turnover_cost_per_employee}
        onChange={(v) => update("turnover_cost_per_employee", v)}
        step="1000"
        testId="input-turnover-cost"
        hint="Recruiting, training, lost productivity, dropped margin."
      />
      <div className="flex flex-col gap-2">
        <HoverTip
          id="retention_probability_boost"
          label={`Retention probability boost — ${Math.round(inputs.retention_probability_boost * 100)}%`}
        />
        <Slider
          data-testid="slider-retention-boost"
          value={[inputs.retention_probability_boost * 100]}
          min={0}
          max={100}
          step={5}
          onValueChange={(val) => update("retention_probability_boost", val[0] / 100)}
          className="mt-2"
        />
        <p className="text-xs text-brand-muted">How much the IBC plan reduces turnover risk.</p>
      </div>
    </Section>
  );
}

function DividendSection({ inputs, update }) {
  return (
    <Section
      icon={TrendUp}
      title="Dividend Projection"
      subtitle="2026 participating dividend scale — set within 6.25% – 6.40%."
    >
      <div className="flex flex-col gap-2 md:col-span-2">
        <HoverTip
          id="dividend_rate"
          label={`Dividend rate — ${(inputs.dividend_rate * 100).toFixed(2)}%`}
        />
        <Slider
          data-testid="slider-dividend-rate"
          value={[inputs.dividend_rate * 10000]}
          min={625}
          max={640}
          step={1}
          onValueChange={(val) => update("dividend_rate", val[0] / 10000)}
          className="mt-2"
        />
        <div className="flex justify-between text-xs text-brand-muted">
          <span>6.25%</span><span>Mid-scale 6.32%</span><span>6.40%</span>
        </div>
      </div>
    </Section>
  );
}

function AdvancedSection({ inputs, update, open, onOpenChange }) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          data-testid="toggle-advanced"
          className="w-full flex items-center justify-between bg-brand-cream/50 border border-brand-line rounded-md px-6 py-4 text-brand-forest font-semibold hover:bg-brand-cream transition-colors"
        >
          <span className="flex items-center gap-3">
            <Gear size={18} weight="duotone" />
            Advanced assumptions
          </span>
          <CaretDown
            size={16}
            weight="bold"
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">
        <Section
          icon={Gear}
          title="Tax & Return Assumptions"
          subtitle="Override defaults if your client's situation differs from baseline."
        >
          <Field
            id="passive_tax_rate_pct"
            helpId="passive_tax_rate"
            label="Passive income tax rate"
            suffix="%"
            value={(inputs.passive_tax_rate * 100).toFixed(2)}
            onChange={(v) => update("passive_tax_rate", (v || 0) / 100)}
            step="0.01"
            testId="input-passive-tax-rate"
            hint="Ontario CCPC combined rate on passive income (default 50.17%)."
          />
          <Field
            id="passive_return_rate_pct"
            helpId="passive_return_rate"
            label="Baseline corporate return (GIC / HISA)"
            suffix="%"
            value={(inputs.passive_return_rate * 100).toFixed(2)}
            onChange={(v) => update("passive_return_rate", (v || 0) / 100)}
            step="0.1"
            testId="input-passive-return-rate"
            hint="Pre-tax return on the 'borrow from bank' path."
          />
        </Section>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function CalcForm({ inputs, setInputs, onCalculate, loading }) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const update = (key, value) =>
    setInputs((prev) => ({ ...prev, [key]: value === "" ? 0 : value }));

  return (
    <form
      data-testid="calc-form"
      onSubmit={(e) => { e.preventDefault(); onCalculate(); }}
      className="space-y-6"
    >
      <PassiveIncomeSection inputs={inputs} update={update} />
      <InterestRecaptureSection inputs={inputs} update={update} />
      <RetentionSection inputs={inputs} update={update} />
      <DividendSection inputs={inputs} update={update} />
      <AdvancedSection inputs={inputs} update={update} open={advancedOpen} onOpenChange={setAdvancedOpen} />

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <Button
          type="submit"
          data-testid="btn-calculate"
          disabled={loading}
          className="bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md h-12 px-8 text-sm font-semibold"
        >
          {loading ? "Calculating…" : "Run breakdown"}
        </Button>
      </div>
    </form>
  );
}
